import type { PersonaMetadata } from "@/types/api/Persona"
import { isSituateSpeakerPresetId } from "@/types/ModelConfigs/VoiceModelConfigs"

import { isVoiceLanguageCombinationSupported } from "./personaModalUtils"
import { validatePersonaForm, type PersonaFormData } from "./personaValidation"

interface PersonaSubmitPayload {
  display_name: string
  avatar_url: string
  metadata: Record<string, unknown>
  simulated_user_prompt: string | undefined
  voice_name: string | null | undefined
  language_code: string | undefined
}

interface InvalidPersonaSubmissionResult {
  status: "invalid-form"
}

interface MissingVoiceConfigurationResult {
  status: "missing-voice-configuration"
}

interface UnsupportedVoiceConfigurationResult {
  status: "unsupported-voice-configuration"
}

interface ValidPersonaSubmissionResult {
  status: "valid"
  payload: PersonaSubmitPayload
}

type PersonaSubmissionResult =
  | InvalidPersonaSubmissionResult
  | MissingVoiceConfigurationResult
  | UnsupportedVoiceConfigurationResult
  | ValidPersonaSubmissionResult

export const getPersonaSubmissionValidationIssue = (
  status: PersonaSubmissionResult["status"],
): string | undefined => {
  switch (status) {
    case "invalid-form":
    case "valid":
      return undefined
    case "missing-voice-configuration":
      return "Select a voice and language."
    case "unsupported-voice-configuration":
      return "Choose a supported voice and accent combination."
    default: {
      const exhaustiveStatus: never = status
      return exhaustiveStatus
    }
  }
}

export const getPersonaSubmissionResult = (
  formData: PersonaFormData,
  isInSilentMode: boolean,
): PersonaSubmissionResult => {
  const { isValid, validatedData } = validatePersonaForm(formData)

  if (!isValid || !validatedData) {
    return { status: "invalid-form" }
  }

  const submitMetadata = validatedData.metadata as PersonaMetadata | undefined
  const hasCustomVoice = Boolean(submitMetadata?.custom_voice_id)

  if (
    !isInSilentMode &&
    !hasCustomVoice &&
    (!submitMetadata?.voice_name || !submitMetadata?.language_code)
  ) {
    return { status: "missing-voice-configuration" }
  }

  if (
    !isInSilentMode &&
    !hasCustomVoice &&
    !isVoiceLanguageCombinationSupported(
      submitMetadata?.voice_name,
      submitMetadata?.language_code,
    )
  ) {
    return { status: "unsupported-voice-configuration" }
  }

  const {
    voice_name,
    language_code,
    situate_speaker,
    ...metadataWithoutVoice
  } = submitMetadata || {}
  const metadataPayload = metadataWithoutVoice as Record<string, unknown>
  delete metadataPayload.voice_profile

  if (isSituateSpeakerPresetId(situate_speaker)) {
    metadataPayload.situate_speaker = situate_speaker
  }

  return {
    status: "valid",
    payload: {
      display_name: validatedData.display_name.trim(),
      avatar_url: validatedData.avatar_url,
      metadata: metadataPayload,
      simulated_user_prompt: validatedData.simulated_user_prompt,
      voice_name: hasCustomVoice ? null : voice_name,
      language_code,
    },
  }
}
